import { addQueryString, fetchJson as fetch } from '@primitives/fetch.utils'
import { getHost, type Chain, type Options } from '..'
import { type Endpoint } from '../llamma'
import * as Schema from './schema'
export { tvl } from './util'

export type * from './schema'

export const USER_MARKETS_FIRST_PAGE = 1
export const USER_MARKETS_DEFAULT_PER_PAGE = 100

export type PaginatedOptions = Options & { page?: number; perPage?: number }

type GetUserMarketsParams = { page?: number; per_page?: number; include_closed?: boolean }

export async function getChains(options?: Options): Promise<Chain[]> {
  const host = getHost(options)

  return fetch(`${host}/v1/lending/chains`).then(resp => Schema.getChainsResponse.parse(resp))
}

export async function getAllMarkets(params: { page?: number; per_page?: number } = {}, options?: Options) {
  const host = getHost(options)
  const response = await fetch(`${host}/v1/lending/markets${addQueryString(params)}`)

  return Schema.getAllMarketsResponse.parse(response)
}

export async function getMarkets(
  blockchainId: Chain,
  params: { page?: number; per_page?: number } = {},
  options?: Options,
) {
  const host = getHost(options)
  const response = await fetch(`${host}/v1/lending/markets/${blockchainId}${addQueryString(params)}`)

  return Schema.getMarketsResponse.parse(response)
}

export async function getSnapshots(
  blockchainId: Chain,
  marketController: string,
  params: { agg?: string; fetch_on_chain?: boolean; limit?: number; start?: number; end?: number } = {
    fetch_on_chain: true,
    agg: 'day',
    limit: 100,
  },
  options?: Options,
) {
  const host = getHost(options)
  const response = await fetch(
    `${host}/v1/lending/markets/${blockchainId}/${marketController}/snapshots${addQueryString(params)}`,
  )

  return Schema.getSnapshotsResponse.parse(response)
}

export async function getAllUserMarkets(
  userAddr: string,
  params: { include_closed?: boolean } = { include_closed: false },
  options?: Options,
) {
  const host = getHost(options)
  const response = await fetch(`${host}/v1/lending/users/all/${userAddr}${addQueryString(params)}`)

  return Schema.getAllUserMarketsResponse.parse(response)
}

export async function getUserMarkets(
  userAddr: string,
  blockchainId: Chain,
  {
    page = USER_MARKETS_FIRST_PAGE,
    per_page = USER_MARKETS_DEFAULT_PER_PAGE,
    include_closed = false,
  }: GetUserMarketsParams = {},
  options?: Options,
) {
  const host = getHost(options)
  const response = await fetch(
    `${host}/v1/lending/users/${blockchainId}/${userAddr}${addQueryString({ page, per_page, include_closed })}`,
  )

  return Schema.getUserMarketsResponse.parse(response)
}

export async function getAllUserLendingPositions(
  userAddr: string,
  params: { include_closed?: boolean } = { include_closed: false },
  options?: Options,
) {
  const host = getHost(options)
  const response = await fetch(`${host}/v1/lending/users/lending_positions/all/${userAddr}${addQueryString(params)}`)

  return Schema.getAllUserLendingPositionsResponse.parse(response)
}

export async function getUserLendingPositions(
  userAddr: string,
  blockchainId: Chain,
  {
    page = USER_MARKETS_FIRST_PAGE,
    per_page = USER_MARKETS_DEFAULT_PER_PAGE,
    include_closed = false,
  }: GetUserMarketsParams = {},
  options?: Options,
) {
  const host = getHost(options)
  const response = await fetch(
    `${host}/v1/lending/users/lending_positions/${blockchainId}/${userAddr}${addQueryString({ page, per_page, include_closed })}`,
  )

  return Schema.getUserLendingPositionsResponse.parse(response)
}

export async function getUserMarketStats(
  userAddr: string,
  blockchainId: Chain,
  marketController: string,
  options?: Options,
) {
  const host = getHost(options)
  const response = await fetch(`${host}/v1/lending/users/${blockchainId}/${userAddr}/${marketController}/stats`)

  return Schema.getUserMarketStatsResponse.parse(response)
}

export async function getMarketUsers(endpoint: Endpoint, blockchainId: Chain, controller: string, options?: Options) {
  const host = getHost(options)
  const response = await fetch(`${host}/v1/${endpoint}/users/${blockchainId}/${controller}/users`)

  return Schema.getMarketUsersResponse.parse(response)
}

export async function getUserMarketEarnings(
  userAddr: string,
  blockchainId: Chain,
  vaultAddress: string,
  options?: Options,
) {
  const host = getHost(options)
  const response = await fetch(`${host}/v1/lending/vaults/${blockchainId}/${vaultAddress}/earnings/${userAddr}`)

  return Schema.getUserMarketEarningsResponse.parse(response)
}

export async function getMarketBorrowers(
  blockchainId: Chain,
  controller: string,
  options: PaginatedOptions & { endpoint?: Endpoint } = {},
) {
  const { endpoint = 'lending', page = 1, perPage = 10, ...requestOptions } = options
  const host = getHost(requestOptions)
  const response = await fetch(
    `${host}/v1/${endpoint}/markets/${blockchainId}/${controller}/borrowers${addQueryString({ page, per_page: perPage })}`,
    { signal: requestOptions.signal },
  )

  return Schema.getMarketBorrowersResponse.parse(response)
}

export async function getVaultDepositors(blockchainId: Chain, vaultAddress: string, options: PaginatedOptions = {}) {
  const { page = 1, perPage = 10, ...requestOptions } = options
  const host = getHost(requestOptions)
  const response = await fetch(
    `${host}/v1/lending/vaults/${blockchainId}/${vaultAddress}/depositors${addQueryString({ page, per_page: perPage })}`,
    { signal: requestOptions.signal },
  )

  return Schema.getVaultDepositorsResponse.parse(response)
}

export async function getVaultEvents(blockchainId: Chain, vaultAddress: string, options: PaginatedOptions = {}) {
  const { page = 1, perPage = 10, ...requestOptions } = options
  const host = getHost(requestOptions)
  const response = await fetch(
    `${host}/v1/lending/vault_events/${blockchainId}/${vaultAddress}${addQueryString({ page, per_page: perPage })}`,
    { signal: requestOptions.signal },
  )

  return Schema.getVaultEventsResponse.parse(response)
}

export async function getUserMarketSnapshots(
  userAddr: string,
  blockchainId: Chain,
  marketController: string,
  options?: Options,
) {
  const host = getHost(options)
  const response = await fetch(
    `${host}/v1/lending/users/${blockchainId}/${userAddr}/${marketController}/snapshots?page=1&per_page=100`,
  )

  return Schema.getUserMarketSnapshotsResponse.parse(response)
}

export async function getUserMarketCollateralEvents(
  userAddr: string,
  blockchainId: Chain,
  marketController: string,
  options?: Options,
) {
  const host = getHost(options)
  const response = await fetch(`${host}/v1/lending/collateral_events/${blockchainId}/${marketController}/${userAddr}`)

  return Schema.getUserCollateralEventsResponse.parse(response)
}

export async function getUserVaultEvents(
  userAddr: string,
  blockchainId: Chain,
  vaultAddress: string,
  options?: Options,
) {
  const host = getHost(options)
  const response = await fetch(`${host}/v1/lending/vaults/${blockchainId}/${vaultAddress}/${userAddr}`, {
    signal: options?.signal,
  })
  return Schema.getUserVaultEventsResponse.parse(response)
}
